import Axios from 'axios'
import { CHECKOUT_VERSION_FIELD } from '@/services/Configuration'

import {
  StencilApiHeaders,
  areAllShipmentsAssigned,
} from '@/services/Helpers'

import {
  standardizeAddressForBigC,
  standardizeOrderFromResponse,
  MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing,
} from '@/services/Standardizers'

import { isSameAddress } from '@/helpers/'

import { log, error } from '@/services/Log'

import { recoverAvailableShippingOptions } from './index'
//---------------------------------------------------------------

const customFieldCheckoutVersion = {
  fieldId: CHECKOUT_VERSION_FIELD,
  fieldValue: '1',
}

export const isOrderUpdatePending = ({ order, address }) => {
  log('isOrderUpdatePending', { order, address })
  const unassignedOptionOrChangedAddress = order.shipments.filter(({
    isBopus, shippingAddress, selectedShippingOption,
  }) => (
    (!selectedShippingOption)
    || (!isBopus && (
      !isSameAddress(address, shippingAddress)
      || address?.street2 !== shippingAddress?.street2
      || address?.phone !== shippingAddress?.phone
      || address?.firstName !== shippingAddress?.firstName
      || address?.lastName !== shippingAddress?.lastName
    )
    )
  )).length > 0

  const orderHasItemOutOfShipments = (
    order.cart.lineItems.filter(({ id }) => ( //  for all cart items
      order.shipments.filter(({ lineItemIds }) => ( //  search in shipments
        ~lineItemIds.indexOf(id) //  for item id in shipment
      )).length === 0 //  If no shipment is found
    )).length > 0 //  Then we have an item unassigned to a shipment
  )

  const newAddressEmail = (
    (address?.email && !order?.cart?.email)
    || (address?.email && order?.cart?.email && address?.email !== order?.cart?.email)
  )

  const cartTimeOut = (
    order?.cart?.updatedTime
    && ((new Date()) / 1000 - (new Date(order?.cart?.updatedTime)) / 1000) > (60 * 60)
  ) // One hour

  const shouldUpdateShippingAddress = (
    unassignedOptionOrChangedAddress
    || orderHasItemOutOfShipments
    || newAddressEmail
    || cartTimeOut
  )
  log(`updateShippingAddress=> shouldUpdateShippingAddress = ${shouldUpdateShippingAddress}`, {
    shouldUpdateShippingAddress,
    unassignedOptionOrChangedAddress,
    orderHasItemOutOfShipments,
    newAddressEmail,
    cartTimeOut,
  })
  return shouldUpdateShippingAddress
}

export const createNonBopusConsignments = async ({ order, address }) => {
  let newOrder = { ...order }
  let success = true
  const nonBopusLineItems = order?.cart?.lineItems.filter(({ id: lineItemID }) => (
    // Getting Items that are not part of a BOPUS consignment
    !order?.shipments.filter(
      // Filter BOPUS shipment that has a matching lineItem ID
      ({ isBopus, lineItemIds }) => isBopus && ~lineItemIds.indexOf(lineItemID),
    ).length
  ))

  // We need to handle furniture and non-furniture consignments separately
  // so we get the correct shipping options
  const lineItemTypeGroups = [
    // isFurniture is parsed from the checkout cart object via item per item API requests
    nonBopusLineItems.filter((lineItem) => !lineItem.isFurniture),
    nonBopusLineItems.filter((lineItem) => lineItem.isFurniture),
  ].filter((group) => group.length)

  if ('form_fields' in address) { // signed in address
    address.form_fields = [...(address?.form_fields || []), customFieldCheckoutVersion]
  } else {
    address.customFields = [...(address?.customFields || []), customFieldCheckoutVersion]
  }
  const shippingAddress = standardizeAddressForBigC({
    ...address, email: address?.email || order?.cart?.email,
  })
  // New Orders will be created and the previous ones will disappear from bigC automatically
  const payload = lineItemTypeGroups.map((shipment) => ({
    shippingAddress,
    lineItems: shipment.map(({ id: itemId, quantity }) => ({ itemId, quantity })),
  }))
  if (payload.length) {
    // Adds a new Consignment to Checkout. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-consignments/checkoutsconsignmentsbycheckoutidpost
    const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments?include=consignments.availableShippingOptions`
    const options = { headers: StencilApiHeaders('post') }
    try {
      const { data, status } = await Axios.post(url, payload, options)
      success = success && status === 200
      newOrder = standardizeOrderFromResponse({ bigcResponse: data, order })
    } catch (err) {
      error(`updateShippingAddress shipping option selection FAILED${err}`, {
        url,
        payload,
        options,
        order,
        newOrder,
      })
      await MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing({ order: newOrder })
      return { success: false, newOrder: { ...order } }
    }
  }
  await MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing({ order: newOrder })
  return { newOrder, success }
}

export const updateShippingAddress = async ({ order, address }) => {
  /*
      3 Steps
      1 - Check if the update is necessary
      2 - Create the consignments
      3 - Update the consignments with the shipping option
    */
  // cart - for line items not yet in shipments
  // current shipments - for BOPUS, etc.
  log('updateShippingAddress START', { order, address })

  const invalidAddress = checkIfAddressInvalid({address, order})
  if (invalidAddress) {
    log("User has put in an invalid destination", address)
    return { success: false, newOrder: order}
  }

  const shouldUpdateShippingAddress = isOrderUpdatePending({ order, address })
  // Step 2
  if (!shouldUpdateShippingAddress) {
    log('unnecessary updateShippingAddress')
    return { success: true, newOrder: order }
  }
  let { newOrder, success } = await createNonBopusConsignments({ order, address })
  log('Update Shipping Address Step 2 Done', { newOrder, success })
  // Step 3
  const shipmentsForUpdate = (
    newOrder?.shipments //  Consignments created
    || order?.shipments //  An order with only bopus won't create new consignments
  )
  const PUTHeaders = StencilApiHeaders('put')
  for (let i = 0; i < shipmentsForUpdate.length; i++) {
    // Shipment object comes from the last PUT request or form the POST request
    const shipmentId = shipmentsForUpdate[i]?.id
    let shipment = (
      newOrder?.shipments
      || shipmentsForUpdate
    ).find(({ id }) => id === shipmentId)

    // Checking for cache failure on bigCommerce
    if (!shipment?.availableShippingOptions.length) {
      log('updateShippingAddress => CACHE failure', { shipment, newOrder, order })
      const recoveredOptions = await recoverAvailableShippingOptions({
        order: newOrder, shipmentId,
      })
      if (recoveredOptions?.success) {
        shipment = recoveredOptions?.shipment
        newOrder = recoveredOptions?.newOrder
      } else {
        error('Failed to recover from cache failure', { order, newOrder, recoveredOptions })
        return { success: false }
      }
    }
    // Trying to identify a previously selected shipping option
    const originallySelectedShippingOption = order?.shipments.find(({ lineItemIds }) => (
      // Searching for a shipment that has the same lineItemIds
      lineItemIds?.length === shipment?.lineItemIds?.length
      && JSON.stringify(shipment?.lineItemIds?.sort()) === JSON.stringify(lineItemIds?.sort())
    ))?.selectedShippingOption
    const shippingOptionId = ( // Try to get originally selected option
      shipment.availableShippingOptions.find(({ description }) => (
        description === originallySelectedShippingOption?.description
      ))?.id
      || shipment?.availableShippingOptions?.[0]?.id
    )
    if (
      shippingOptionId
      && order?.cart?.id
      && (
        !shipment?.selectedShippingOption
        || !originallySelectedShippingOption?.description
        || originallySelectedShippingOption?.description !== shipment?.selectedShippingOption?.description
      )) {
      // Updates an existing consignment. https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-consignments/checkoutsconsignmentsbycheckoutidandconsignmentidput
      // Shipping address line item IDs or the shipping option ID can be updated using this endpoint
      // One or more of these three fields are mandatory.
      // Shipping address and line items can be updated in one request.
      const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`
      const payload = { shippingOptionId }
      const options = { headers: PUTHeaders }
      try {
        const { data, status } = await Axios.put(url, payload, options)
        success = success && status === 200
        newOrder = standardizeOrderFromResponse({ bigcResponse: data, order })
      } catch {
        error('updateShippingAddress shipping option selection FAILED', {
          url,
          payload,
          options,
          originallySelectedShippingOption,
          shipment,
          order,
          newOrder,
          shipmentIndex: i,
        })
        return { success: false }
      }
    } else {
      log('updateShippingAddress => No update for this shipment', { shipment })
    }
  }

  log('updateShippingAddress END', { success, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
  return { success, newOrder }
}


const checkIfAddressInvalid = ({
  order,
  address
}) => {
  try {
    for (const item of order.cart.lineItems) {
      if (item.isFurniture) {
        if (['HI', 'AK'].includes(address.stateAcronym)) {
          return true
        }
      }
    }
  } catch {}
  return false
}